import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi, calendarSyncApi, dealerCreditApi, api } from '../services/api';
import { User } from '../types';
import { PRESETS, loadThemePref, saveThemePrefLocal, normalizePref, resolveColors, previewSwatch, isValidHex, type ThemePref } from '../utils/theme';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const getToken = () => localStorage.getItem('dealer_auth_token') || localStorage.getItem('auth_token');
const apiFetch = async (path: string, opts: RequestInit = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...opts.headers },
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || 'Request failed'); }
  if (res.status === 204) return null;
  return res.json();
};

function QuoteDefaultsSection({ dealerId }: { dealerId?: string }) {
  const [salesTax, setSalesTax] = useState(0);
  const [installCost, setInstallCost] = useState(0);
  const [markup, setMarkup] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!dealerId) return;
    apiFetch(`/dealers/${dealerId}/quote-defaults`).then(d => {
      if (d) { setSalesTax(d.salesTax || 0); setInstallCost(d.installationCost || 0); setMarkup(d.markup || 0); }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [dealerId]);

  const handleSave = async () => {
    if (!dealerId) return;
    setSaving(true); setSaved(false);
    try {
      await apiFetch(`/dealers/${dealerId}/quote-defaults`, {
        method: 'PATCH',
        body: JSON.stringify({ salesTax, installationCost: installCost, markup }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  if (!loaded) return <p className="text-sm text-gray-400">Loading...</p>;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sales Tax Rate (%)</label>
        <input type="number" min="0" max="20" step="0.01" value={salesTax || ''} onChange={e => setSalesTax(parseFloat(e.target.value) || 0)}
          placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-yellow-500 focus:border-yellow-500" />
        <p className="text-xs text-gray-400 mt-1">Auto-populates on Quote Calculator</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Installation ($ per unit)</label>
        <input type="number" min="0" step="1" value={installCost || ''} onChange={e => setInstallCost(parseFloat(e.target.value) || 0)}
          placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-yellow-500 focus:border-yellow-500" />
        <p className="text-xs text-gray-400 mt-1">Default install cost per screen</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">MSRP Markup (%)</label>
        <input type="number" min="0" max="100" step="0.5" value={markup || ''} onChange={e => setMarkup(parseFloat(e.target.value) || 0)}
          placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-yellow-500 focus:border-yellow-500" />
        <p className="text-xs text-gray-400 mt-1">Added to MSRP for customer quotes</p>
      </div>
      <div className="sm:col-span-3 flex items-center gap-3">
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400 text-sm disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Defaults'}
        </button>
        {saved && <span className="text-green-600 text-sm">Saved!</span>}
      </div>
    </div>
  );
}

function PricingDisplaySection({ dealerId }: { dealerId?: string }) {
  const [pricingDisplay, setPricingDisplay] = useState<'both' | 'dealer_only' | 'msrp_only'>('both');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!dealerId) return;
    apiFetch(`/dealers/${dealerId}/quote-defaults`).then(d => {
      if (d?.pricingDisplay) setPricingDisplay(d.pricingDisplay);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [dealerId]);

  const handleSave = async (value: string) => {
    setPricingDisplay(value as any);
    if (!dealerId) {
      console.error('Cannot save pricing display: dealerId is missing');
      return;
    }
    setSaving(true); setSaved(false);
    try {
      await apiFetch(`/dealers/${dealerId}/quote-defaults`, {
        method: 'PATCH',
        body: JSON.stringify({ pricingDisplay: value }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      console.error('Failed to save pricing display:', err);
    } finally { setSaving(false); }
  };

  if (!loaded) return <p className="text-sm text-gray-400">Loading...</p>;

  const options = [
    { value: 'both', label: 'Both (with toggle)', desc: 'Toggle between dealer and customer pricing on quote pages' },
    { value: 'dealer_only', label: 'Dealer Price Only', desc: 'Show only your dealer cost — no MSRP or customer pricing' },
    { value: 'msrp_only', label: 'Customer Price Only (MSRP)', desc: 'Show only MSRP pricing — safe to show to customers on screen' },
  ];

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">Controls how prices appear on the Quote Calculator and Price Builder pages.</p>
      {options.map(opt => (
        <label key={opt.value}
          className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
            pricingDisplay === opt.value
              ? 'border-yellow-500 bg-yellow-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <input
            type="radio" name="pricingDisplay" value={opt.value}
            checked={pricingDisplay === opt.value}
            onChange={() => handleSave(opt.value)}
            className="mt-0.5 accent-yellow-500"
          />
          <div>
            <div className="text-sm font-medium text-gray-800">{opt.label}</div>
            <div className="text-xs text-gray-500">{opt.desc}</div>
          </div>
        </label>
      ))}
      {saving && <span className="text-xs text-gray-400">Saving...</span>}
      {saved && <span className="text-xs text-green-600">Saved!</span>}
    </div>
  );
}

function SmsNotificationsSection() {
  const [phone, setPhone] = useState('');
  const [txOptIn, setTxOptIn] = useState(false);
  const [mktOptIn, setMktOptIn] = useState(false);
  const [consentAt, setConsentAt] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    apiFetch('/dealers/my/sms-preferences').then(d => {
      if (d) {
        setPhone(d.phone || '');
        setTxOptIn(!!d.transactional);
        setMktOptIn(!!d.marketing);
        setConsentAt(d.consentAt || null);
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const digits = phone.replace(/\D/g, '');
  const phoneValid = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));

  const handleSave = async () => {
    setErr(null);
    if ((txOptIn || mktOptIn) && !phoneValid) {
      setErr('Enter a valid US mobile number to receive text messages.');
      return;
    }
    setSaving(true); setSaved(false);
    try {
      const d = await apiFetch('/dealers/my/sms-preferences', {
        method: 'PATCH',
        body: JSON.stringify({ phone, transactional: txOptIn, marketing: mktOptIn }),
      });
      if (d?.consentAt) setConsentAt(d.consentAt);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setErr(e.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p className="text-sm text-gray-400">Loading...</p>;

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Mobile number for text messages</label>
        <input
          type="tel"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="(555) 555-1234"
          className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-yellow-500 focus:border-yellow-500"
        />
        {phone && !phoneValid && <p className="text-xs text-red-500 mt-1">Enter a valid US mobile number.</p>}
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={txOptIn} onChange={e => setTxOptIn(e.target.checked)} className="mt-1 accent-yellow-500" />
        <span className="text-sm text-gray-700">
          <span className="font-medium">Order updates (transactional).</span> Text me status updates for my own orders — order ready, balance due, shipped, and delivered.
        </span>
      </label>

      <p className="text-xs text-gray-500 leading-relaxed border-t border-gray-100 pt-3">
        By checking the box above and saving, you agree to receive recurring automated text messages from Keder Screens about your
        own orders (order ready, balance due, shipped, and delivered) at the number provided. These are transactional order
        notifications only — no marketing or promotional texts. Consent is not a condition of purchase. Message frequency may vary.
        Standard message and data rates may apply. Reply STOP to opt out. Reply HELP for assistance. Your mobile information will not
        be sold or shared with third parties for promotional or marketing purposes. See our{' '}
        <a href="https://kederscreens.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="underline">Terms &amp; Conditions</a>{' '}
        and{' '}
        <a href="https://kederscreens.com/privacy/" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>.
      </p>

      {consentAt && (txOptIn || mktOptIn) && (
        <p className="text-xs text-gray-400">Consent recorded {new Date(consentAt).toLocaleDateString()}.</p>
      )}
      {err && <p className="text-sm text-red-600">{err}</p>}

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400 text-sm disabled:opacity-50">
          {saving ? 'Saving...' : 'Save SMS Preferences'}
        </button>
        {saved && <span className="text-green-600 text-sm">Saved!</span>}
      </div>
    </div>
  );
}

function AppearanceSection() {
  const [pref, setPref] = useState<ThemePref>(() => loadThemePref());
  const [savedTick, setSavedTick] = useState(false);

  useEffect(() => {
    api.getUiTheme().then((r) => { if (r) setPref(normalizePref(r)); }).catch(() => {});
  }, []);

  const choose = (p: ThemePref) => {
    setPref(p);
    saveThemePrefLocal(p);            // apply + cache instantly (no flash)
    setSavedTick(true);
    window.setTimeout(() => setSavedTick(false), 1600);
    api.updateUiTheme(p).catch(() => {}); // durable per-user copy, fire-and-forget
  };

  const selectPreset = (id: string) => {
    const preset = PRESETS.find((p) => p.id === id)!;
    choose({ preset: id, customBg: preset.bg, customAccent: preset.accent });
  };

  // Switching to Custom seeds the pickers from the currently-resolved colors.
  const startCustom = () => {
    const { bg, accent } = resolveColors(pref);
    choose({ preset: 'custom', customBg: bg, customAccent: accent });
  };

  const cur = resolveColors(pref);

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {PRESETS.map((p) => {
          const sw = previewSwatch(p.bg, p.accent);
          const selected = pref.preset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => selectPreset(p.id)}
              className={`text-left rounded-lg border-2 p-1.5 transition ${selected ? 'border-yellow-500 ring-2 ring-yellow-300' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="h-12 rounded-md relative overflow-hidden border border-black/10" style={{ backgroundColor: sw.page }}>
                <div className="absolute left-0 top-0 bottom-0 w-2.5" style={{ backgroundColor: sw.sidebar }} />
                <div className="absolute left-3.5 top-2 right-2 h-3 rounded-sm" style={{ backgroundColor: sw.card }} />
                <div className="absolute right-2 bottom-2 w-4 h-4 rounded-full" style={{ backgroundColor: sw.accent }} />
              </div>
              <div className="text-[11px] font-medium text-gray-700 mt-1 px-0.5 truncate">{p.label}</div>
            </button>
          );
        })}

        {/* Custom */}
        <button
          type="button"
          onClick={startCustom}
          className={`text-left rounded-lg border-2 p-1.5 transition ${pref.preset === 'custom' ? 'border-yellow-500 ring-2 ring-yellow-300' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <div className="h-12 rounded-md relative overflow-hidden border border-black/10 flex items-center justify-center"
            style={{ background: 'conic-gradient(#ef4444,#f59e0b,#eab308,#22c55e,#3b82f6,#8b5cf6,#ef4444)' }}>
            <span className="text-white text-lg drop-shadow">🎨</span>
          </div>
          <div className="text-[11px] font-medium text-gray-700 mt-1 px-0.5">Custom</div>
        </button>
      </div>

      {pref.preset === 'custom' && (
        <div className="mt-4 flex flex-wrap items-center gap-6 p-3 rounded-lg bg-gray-50 border border-gray-200">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Background
            <input
              type="color"
              value={isValidHex(pref.customBg) ? pref.customBg : '#0a0a0a'}
              onChange={(e) => choose({ ...pref, preset: 'custom', customBg: e.target.value })}
              className="w-10 h-8 rounded border border-gray-300 cursor-pointer p-0"
            />
            <span className="font-mono text-xs text-gray-500">{cur.bg}</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            Accent
            <input
              type="color"
              value={isValidHex(pref.customAccent) ? pref.customAccent : '#FFCD24'}
              onChange={(e) => choose({ ...pref, preset: 'custom', customAccent: e.target.value })}
              className="w-10 h-8 rounded border border-gray-300 cursor-pointer p-0"
            />
            <span className="font-mono text-xs text-gray-500">{cur.accent}</span>
          </label>
        </div>
      )}

      <p className="mt-3 text-xs text-gray-500">
        Pick a preset or build your own (background + accent — the rest is derived). The ☀️/🌙 button in the top bar flips light/dark and remembers your chosen theme for each.{' '}
        {savedTick && <span className="text-green-600 font-medium">✓ Saved &amp; synced.</span>}
      </p>
    </div>
  );
}

export const ProfilePage: React.FC = () => {
  const { user: authUser, refreshUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Password change form
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  // Address edit form
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
  });
  const [addressErrors, setAddressErrors] = useState<Record<string, string>>({});

  // Calendar sync
  const [calendarToken, setCalendarToken] = useState<string>('');
  const [subscriptionUrl, setSubscriptionUrl] = useState<string>('');
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Logo upload
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [website, setWebsite] = useState('');
  const [websiteEditing, setWebsiteEditing] = useState(false);
  const [websiteSaving, setWebsiteSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Credit account
  const [creditAccount, setCreditAccount] = useState<{
    netAccountEnabled: boolean;
    creditLimit: number;
    creditBalance: number;
    creditAvailable: number;
    netTermsDays: number;
    accountStatus: string;
    hasOverdue: boolean;
    oldestOverdueDays: number | null;
  } | null>(null);

  // API Key
  const [apiKeyStatus, setApiKeyStatus] = useState<{ hasKey: boolean; createdAt?: string } | null>(null);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);

  // Webhooks
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [webhookForm, setWebhookForm] = useState({ url: '', label: '', events: [] as string[] });
  const [webhookSaving, setWebhookSaving] = useState(false);

  // Email Config
  const [emailConfig, setEmailConfig] = useState<any>(null);
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState({ host: '', port: 587, user: '', password: '', fromEmail: '', fromName: '' });
  const [emailSaving, setEmailSaving] = useState(false);

  // Integration Guide Modal
  const [showGuide, setShowGuide] = useState(false);

  const WEBHOOK_EVENTS = [
    'order.submitted', 'order.in_production', 'order.ready', 'order.shipped', 'order.delivered',
    'order.needs_correction', 'payment.received', 'lead.created', 'lead.updated',
  ];

  useEffect(() => {
    loadProfile();
    loadCalendarToken();
    loadCreditAccount();
    loadApiKeyStatus();
    loadWebhooks();
    loadEmailConfig();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profile = await authApi.getProfile();
      setUser(profile);
      // Set logo URL from profile
      setLogoUrl((profile as any)?.logoUrl || null);
      // Load website from branding
      if ((profile as any)?.dealerId) {
        try {
          const token = localStorage.getItem('auth_token');
          const res = await fetch(`/api/dealers/${(profile as any).dealerId}/branding`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const branding = await res.json();
            setWebsite(branding.website || '');
          }
        } catch {}
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadCalendarToken = async () => {
    try {
      setLoadingCalendar(true);
      const data = await calendarSyncApi.getToken();
      setCalendarToken(data.token);
      setSubscriptionUrl(data.subscriptionUrl);
    } catch (err: any) {
      console.error('Failed to load calendar token:', err);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const loadCreditAccount = async () => {
    try {
      const data = await dealerCreditApi.getMyAccount();
      setCreditAccount(data);
    } catch (err: any) {
      console.debug('Credit account not available:', err.message);
    }
  };

  const loadApiKeyStatus = async () => {
    try {
      const data = await apiFetch('/auth/api-key');
      setApiKeyStatus(data);
    } catch { setApiKeyStatus({ hasKey: false }); }
  };

  const generateApiKey = async () => {
    if (apiKeyStatus?.hasKey && !confirm('This will invalidate your existing API key. Continue?')) return;
    setApiKeyLoading(true);
    try {
      const data = await apiFetch('/auth/api-key', { method: 'POST' });
      setGeneratedKey(data.apiKey);
      setApiKeyStatus({ hasKey: true, createdAt: new Date().toISOString() });
      setSuccess('API key generated! Copy it now — it won\'t be shown again.');
    } catch (err: any) { setError(err.message); }
    finally { setApiKeyLoading(false); }
  };

  const revokeApiKey = async () => {
    if (!confirm('Revoke your API key? Any integrations using it will stop working.')) return;
    setApiKeyLoading(true);
    try {
      await apiFetch('/auth/api-key', { method: 'DELETE' });
      setApiKeyStatus({ hasKey: false });
      setGeneratedKey(null);
      setSuccess('API key revoked.');
    } catch (err: any) { setError(err.message); }
    finally { setApiKeyLoading(false); }
  };

  const loadWebhooks = async () => {
    try {
      setWebhooksLoading(true);
      const data = await apiFetch('/dealer/webhooks');
      setWebhooks(data || []);
    } catch { setWebhooks([]); }
    finally { setWebhooksLoading(false); }
  };

  const saveWebhook = async () => {
    if (!webhookForm.url) return;
    setWebhookSaving(true);
    try {
      await apiFetch('/dealer/webhooks', { method: 'POST', body: JSON.stringify(webhookForm) });
      setShowWebhookForm(false);
      setWebhookForm({ url: '', label: '', events: [] });
      setSuccess('Webhook created!');
      loadWebhooks();
    } catch (err: any) { setError(err.message); }
    finally { setWebhookSaving(false); }
  };

  const deleteWebhook = async (id: string) => {
    if (!confirm('Delete this webhook?')) return;
    try {
      await apiFetch(`/dealer/webhooks/${id}`, { method: 'DELETE' });
      setSuccess('Webhook deleted.');
      loadWebhooks();
    } catch (err: any) { setError(err.message); }
  };

  const testWebhook = async (id: string) => {
    try {
      await apiFetch(`/dealer/webhooks/${id}/test`, { method: 'POST' });
      setSuccess('Test webhook sent!');
    } catch (err: any) { setError(err.message); }
  };

  const rotateWebhookSecret = async (id: string) => {
    if (!confirm('Rotate the signing secret? You\'ll need to update your verification code.')) return;
    try {
      const data = await apiFetch(`/dealer/webhooks/${id}/rotate-secret`, { method: 'POST' });
      setSuccess('Secret rotated. New secret: ' + data.secret);
      loadWebhooks();
    } catch (err: any) { setError(err.message); }
  };

  const loadEmailConfig = async () => {
    try {
      setEmailConfigLoading(true);
      const data = await apiFetch('/dealer/email-config');
      setEmailConfig(data);
    } catch { setEmailConfig(null); }
    finally { setEmailConfigLoading(false); }
  };

  const saveEmailConfig = async () => {
    setEmailSaving(true);
    try {
      await apiFetch('/dealer/email-config/smtp', { method: 'POST', body: JSON.stringify(emailForm) });
      setShowEmailForm(false);
      setSuccess('Email configuration saved!');
      loadEmailConfig();
    } catch (err: any) { setError(err.message); }
    finally { setEmailSaving(false); }
  };

  const testEmailConfig = async () => {
    try {
      await apiFetch('/dealer/email-config/test', { method: 'POST' });
      setSuccess('Test email sent successfully!');
    } catch (err: any) { setError(err.message); }
  };

  const deleteEmailConfig = async () => {
    if (!confirm('Remove your email configuration? Emails will be sent from the factory default.')) return;
    try {
      await apiFetch('/dealer/email-config', { method: 'DELETE' });
      setEmailConfig(null);
      setSuccess('Email configuration removed.');
    } catch (err: any) { setError(err.message); }
  };

  const handleRegenerateCalendarToken = async () => {
    if (!confirm('Regenerate calendar subscription link? Your old link will stop working and you\'ll need to update it in your calendar apps.')) {
      return;
    }

    try {
      setRegenerating(true);
      const data = await calendarSyncApi.regenerateToken();
      setCalendarToken(data.token);
      setSubscriptionUrl(data.subscriptionUrl);
      setSuccess(data.message);
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate calendar token');
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  // Logo upload handler
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only PNG, JPG, and WEBP are allowed.');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('File too large. Maximum size is 2MB.');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);
      const result = await authApi.uploadLogo(file);
      setLogoUrl(result.logoUrl);
      setSuccess('Logo uploaded successfully!');
      // Refresh profile to update context
      if (refreshUser) refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  // Logo delete handler
  const handleLogoDelete = async () => {
    if (!confirm('Are you sure you want to delete your company logo?')) {
      return;
    }

    try {
      setDeletingLogo(true);
      setError(null);
      await authApi.deleteLogo();
      setLogoUrl(null);
      setSuccess('Logo deleted successfully');
      // Refresh profile to update context
      if (refreshUser) refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete logo');
    } finally {
      setDeletingLogo(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setPasswordErrors({});

    const errors: Record<string, string> = {};

    if (!currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (!newPassword) {
      errors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }

    if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    try {
      setSaving(true);
      await authApi.changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  // Open address edit form
  const openAddressForm = () => {
    const u = user as any;
    setAddressForm({
      street: u?.addressStreet || u?.address_street || '',
      city: u?.addressCity || u?.address_city || '',
      state: u?.addressState || u?.address_state || '',
      zip: u?.addressZip || u?.address_zip || '',
    });
    setAddressErrors({});
    setShowAddressForm(true);
  };

  // Handle address save
  const handleAddressSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAddressErrors({});

    const errors: Record<string, string> = {};

    if (!addressForm.city.trim()) {
      errors.city = 'City is required';
    }

    if (!addressForm.state.trim()) {
      errors.state = 'State is required';
    }

    if (!addressForm.zip.trim()) {
      errors.zip = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(addressForm.zip.trim())) {
      errors.zip = 'Invalid ZIP code format';
    }

    if (Object.keys(errors).length > 0) {
      setAddressErrors(errors);
      return;
    }

    try {
      setSavingAddress(true);
      await authApi.updateAddress({
        street: addressForm.street.trim(),
        city: addressForm.city.trim(),
        state: addressForm.state.trim(),
        zip: addressForm.zip.trim(),
      });

      setSuccess('Address updated successfully');
      setShowAddressForm(false);

      // Refresh profile to get updated data
      await loadProfile();
      if (refreshUser) refreshUser();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update address');
    } finally {
      setSavingAddress(false);
    }
  };

  // Helper to get field value with fallbacks
  const getField = (primary: any, ...fallbacks: any[]) => {
    if (primary) return primary;
    for (const fb of fallbacks) {
      if (fb) return fb;
    }
    return '-';
  };

  // Get phone display
  const getPhone = () => {
    const u = user as any;
    return getField(u?.phone, u?.phoneWork, u?.phone_work, u?.phoneMobile, u?.phone_mobile, u?.phoneHome, u?.phone_home);
  };

  // Get address display
  const getAddress = () => {
    const u = user as any;
    const street = u?.addressStreet || u?.address_street || '';
    const city = u?.addressCity || u?.address_city || '';
    const state = u?.addressState || u?.address_state || '';
    const zip = u?.addressZip || u?.address_zip || '';

    if (!street && !city) return '-';
    return `${street}${street && city ? ', ' : ''}${city}${city && state ? ', ' : ''}${state} ${zip}`.trim();
  };

  // Get company name
  const getCompany = () => {
    const u = user as any;
    return getField(u?.companyName, u?.company, u?.dealerName);
  };

  // Get discount percent
  const getDiscount = () => {
    const u = user as any;
    const discount = u?.discountPercent || u?.discount_percent || u?.discountRate || 0;
    return discount > 0 ? `${discount}%` : 'Standard pricing';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Profile & Settings</h1>
        <p className="text-gray-400">Manage your account settings</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex justify-between items-center">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex justify-between items-center">
          <p className="text-green-600">{success}</p>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700 font-bold">×</button>
        </div>
      )}

      {/* Account Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Account Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-500">Full Name</label>
            <p className="mt-1 text-gray-900 font-medium">
              {getField(user?.name, `${user?.firstName || ''} ${user?.lastName || ''}`.trim())}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Email Address</label>
            <p className="mt-1 text-gray-900">{user?.email || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Phone</label>
            <p className="mt-1 text-gray-900">{getPhone()}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Role</label>
            <p className="mt-1 text-gray-900 capitalize">
              {(user?.role || 'dealer').replace('_', ' ')}
            </p>
          </div>
        </div>
      </div>

      {/* Text Message Notifications */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 3v-3z" />
          </svg>
          Text Message Notifications
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Opt in to receive order update text messages from Keder Screens — transactional order notifications only. Opt out anytime by replying STOP.
        </p>
        <SmsNotificationsSection />
      </div>

      {/* Appearance / Theme */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          Appearance
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Choose how the portal looks. Your choice is saved to your account and follows you across devices.
        </p>
        <AppearanceSection />
      </div>

      {/* Company Logo */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Your Company Logo
        </h2>

        <p className="text-sm text-gray-600 mb-4">
          Upload your company logo to display on orders and quotes. This helps personalize your documents.
        </p>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {/* Logo Preview */}
          <div className="flex-shrink-0">
            {logoUrl ? (
              <div className="relative group">
                <img
                  src={logoUrl}
                  alt="Company Logo"
                  className="w-32 h-32 object-contain border border-gray-200 rounded-lg bg-gray-50"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                  <button
                    onClick={handleLogoDelete}
                    disabled={deletingLogo}
                    className="text-white hover:text-red-300 transition-colors"
                    title="Delete logo"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex-1">
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
              id="logo-upload"
            />
            <div className="space-y-3">
              <label
                htmlFor="logo-upload"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer ${
                  uploadingLogo
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                }`}
              >
                {uploadingLogo ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    {logoUrl ? 'Change Logo' : 'Upload Logo'}
                  </>
                )}
              </label>

              {logoUrl && (
                <button
                  onClick={handleLogoDelete}
                  disabled={deletingLogo}
                  className="ml-3 inline-flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors"
                >
                  {deletingLogo ? 'Deleting...' : 'Remove Logo'}
                </button>
              )}

              <p className="text-xs text-gray-500">
                PNG, JPG, or WEBP. Max 2MB. Recommended: 200x200px or larger, square or landscape.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* White-Label Branding */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
          White-Label Branding
        </h2>

        <p className="text-sm text-gray-600 mb-4">
          When you upload a logo, your dealer portal and customer emails will be branded with your company identity.
        </p>

        <div className="space-y-4">
          {/* Branding Status */}
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${logoUrl ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="text-sm">
              {logoUrl ? 'Your portal is white-labeled with your logo' : 'Upload a logo above to enable white-labeling'}
            </span>
          </div>

          {/* Branded Email */}
          {(user as any)?.brandedEmail ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium text-green-800">Branded Email Active</span>
              </div>
              <p className="text-sm text-green-700">
                Emails to your customers are sent from: <strong>{(user as any).brandedEmail}</strong>
              </p>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium text-gray-600">Branded Email Not Configured</span>
              </div>
              <p className="text-sm text-gray-500">
                Contact Keder Screens to set up a branded email address (e.g., yourcompany@kederscreens.com) for customer communications.
              </p>
            </div>
          )}

          {/* Website */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Website</span>
            </div>
            {websiteEditing ? (
              <div className="flex gap-2">
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://yourcompany.com"
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
                <button
                  onClick={async () => {
                    setWebsiteSaving(true);
                    try {
                      const token = localStorage.getItem('auth_token');
                      await fetch(`/api/dealers/${user?.dealerId}/website`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ website }),
                      });
                      setWebsiteEditing(false);
                    } catch {}
                    setWebsiteSaving(false);
                  }}
                  disabled={websiteSaving}
                  className="px-3 py-1.5 bg-yellow-500 text-black text-sm font-medium rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                >
                  {websiteSaving ? '...' : 'Save'}
                </button>
                <button
                  onClick={() => setWebsiteEditing(false)}
                  className="px-3 py-1.5 text-gray-500 text-sm hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {website ? (
                    <a href={website} target="_blank" rel="noopener noreferrer" className="text-yellow-600 hover:underline">{website}</a>
                  ) : (
                    <span className="text-gray-400">Not set — add your website to show it in emails</span>
                  )}
                </p>
                <button onClick={() => setWebsiteEditing(true)} className="text-xs text-yellow-600 hover:underline ml-2">
                  {website ? 'Edit' : 'Add'}
                </button>
              </div>
            )}
          </div>

          {/* What's branded list */}
          {logoUrl && (
            <div className="text-sm text-gray-600">
              <p className="font-medium mb-2">Your branding is shown on:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-500">
                <li>Portal sidebar and header</li>
                <li>Order confirmation emails</li>
                <li>Invoice emails</li>
                <li>Estimate emails to customers</li>
                <li>Portal footer ("Powered by Keder Screens")</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Dealer Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Dealer Information
          </h2>
          {!showAddressForm && (
            <button
              onClick={openAddressForm}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit Address
            </button>
          )}
        </div>

        {showAddressForm ? (
          <form onSubmit={handleAddressSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Company Name</label>
              <p className="text-gray-900 font-medium text-lg">{getCompany()}</p>
              <p className="text-xs text-gray-500 mt-1">To change your company name, contact dealers@kederscreens.com</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input
                type="text"
                value={addressForm.street}
                onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                placeholder="Street address (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={addressForm.city}
                  onChange={(e) => {
                    setAddressForm({ ...addressForm, city: e.target.value });
                    setAddressErrors({ ...addressErrors, city: '' });
                  }}
                  className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${
                    addressErrors.city ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {addressErrors.city && (
                  <p className="text-red-500 text-sm mt-1">{addressErrors.city}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={addressForm.state}
                  onChange={(e) => {
                    setAddressForm({ ...addressForm, state: e.target.value });
                    setAddressErrors({ ...addressErrors, state: '' });
                  }}
                  className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${
                    addressErrors.state ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select</option>
                  <option value="AL">AL</option>
                  <option value="AK">AK</option>
                  <option value="AZ">AZ</option>
                  <option value="AR">AR</option>
                  <option value="CA">CA</option>
                  <option value="CO">CO</option>
                  <option value="CT">CT</option>
                  <option value="DE">DE</option>
                  <option value="FL">FL</option>
                  <option value="GA">GA</option>
                  <option value="HI">HI</option>
                  <option value="ID">ID</option>
                  <option value="IL">IL</option>
                  <option value="IN">IN</option>
                  <option value="IA">IA</option>
                  <option value="KS">KS</option>
                  <option value="KY">KY</option>
                  <option value="LA">LA</option>
                  <option value="ME">ME</option>
                  <option value="MD">MD</option>
                  <option value="MA">MA</option>
                  <option value="MI">MI</option>
                  <option value="MN">MN</option>
                  <option value="MS">MS</option>
                  <option value="MO">MO</option>
                  <option value="MT">MT</option>
                  <option value="NE">NE</option>
                  <option value="NV">NV</option>
                  <option value="NH">NH</option>
                  <option value="NJ">NJ</option>
                  <option value="NM">NM</option>
                  <option value="NY">NY</option>
                  <option value="NC">NC</option>
                  <option value="ND">ND</option>
                  <option value="OH">OH</option>
                  <option value="OK">OK</option>
                  <option value="OR">OR</option>
                  <option value="PA">PA</option>
                  <option value="RI">RI</option>
                  <option value="SC">SC</option>
                  <option value="SD">SD</option>
                  <option value="TN">TN</option>
                  <option value="TX">TX</option>
                  <option value="UT">UT</option>
                  <option value="VT">VT</option>
                  <option value="VA">VA</option>
                  <option value="WA">WA</option>
                  <option value="WV">WV</option>
                  <option value="WI">WI</option>
                  <option value="WY">WY</option>
                </select>
                {addressErrors.state && (
                  <p className="text-red-500 text-sm mt-1">{addressErrors.state}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={addressForm.zip}
                  onChange={(e) => {
                    setAddressForm({ ...addressForm, zip: e.target.value });
                    setAddressErrors({ ...addressErrors, zip: '' });
                  }}
                  placeholder="12345"
                  className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${
                    addressErrors.zip ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {addressErrors.zip && (
                  <p className="text-red-500 text-sm mt-1">{addressErrors.zip}</p>
                )}
              </div>
            </div>

            <div className="flex space-x-4 pt-2">
              <button
                type="submit"
                disabled={savingAddress}
                className="px-6 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 font-medium transition-colors"
              >
                {savingAddress ? 'Saving...' : 'Save Address'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddressForm(false);
                  setAddressErrors({});
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500">Company Name</label>
                <p className="mt-1 text-gray-900 font-medium text-lg">{getCompany()}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500">Address</label>
                <p className="mt-1 text-gray-900">{getAddress()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Dealer ID</label>
                <p className="mt-1 text-gray-900 font-mono text-sm">{user?.dealerId || '-'}</p>
              </div>
            </div>

            <p className="mt-6 text-sm text-gray-500 border-t pt-4">
              To change your company name, contact us at{' '}
              <a href="mailto:dealers@kederscreens.com" className="text-yellow-600 hover:underline">
                dealers@kederscreens.com
              </a>
            </p>
          </>
        )}
      </div>

      {/* Credit Account (only shown when net account is enabled) */}
      {creditAccount?.netAccountEnabled && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            Net Credit Account
            <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${
              creditAccount.accountStatus === 'active' ? 'bg-green-100 text-green-700' :
              creditAccount.accountStatus === 'frozen' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {creditAccount.accountStatus === 'active' ? 'Active' :
               creditAccount.accountStatus === 'frozen' ? 'Frozen' : 'Suspended'}
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Credit Limit</label>
              <p className="mt-1 text-gray-900 font-medium text-lg">
                ${creditAccount.creditLimit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Balance Used</label>
              <p className="mt-1 text-gray-900 font-medium text-lg">
                ${creditAccount.creditBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Available Credit</label>
              <p className="mt-1 text-green-700 font-bold text-lg">
                ${creditAccount.creditAvailable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all ${
                  creditAccount.creditLimit > 0 && (creditAccount.creditBalance / creditAccount.creditLimit) > 0.9
                    ? 'bg-red-500'
                    : creditAccount.creditLimit > 0 && (creditAccount.creditBalance / creditAccount.creditLimit) > 0.7
                      ? 'bg-yellow-500'
                      : 'bg-green-500'
                }`}
                style={{
                  width: creditAccount.creditLimit > 0
                    ? `${Math.min((creditAccount.creditBalance / creditAccount.creditLimit) * 100, 100)}%`
                    : '0%'
                }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Net Terms: <strong className="text-gray-900">Net {creditAccount.netTermsDays}</strong></span>
            {creditAccount.hasOverdue && (
              <span className="text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                Overdue balance — please contact accounting
              </span>
            )}
          </div>

          {creditAccount.accountStatus === 'frozen' && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Your credit account is currently frozen. New orders will require payment until the account is restored. Please contact us at{' '}
                <a href="mailto:orders@kederscreens.com" className="text-yellow-700 underline">orders@kederscreens.com</a> for assistance.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Password Change */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Password
          </h2>
          {!showPasswordForm && (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Change Password
            </button>
          )}
        </div>

        {showPasswordForm ? (
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={e => {
                  setCurrentPassword(e.target.value);
                  setPasswordErrors(prev => ({ ...prev, currentPassword: '' }));
                }}
                className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${
                  passwordErrors.currentPassword ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {passwordErrors.currentPassword && (
                <p className="text-red-500 text-sm mt-1">{passwordErrors.currentPassword}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={e => {
                  setNewPassword(e.target.value);
                  setPasswordErrors(prev => ({ ...prev, newPassword: '' }));
                }}
                className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${
                  passwordErrors.newPassword ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {passwordErrors.newPassword && (
                <p className="text-red-500 text-sm mt-1">{passwordErrors.newPassword}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => {
                  setConfirmPassword(e.target.value);
                  setPasswordErrors(prev => ({ ...prev, confirmPassword: '' }));
                }}
                className={`w-full px-3 py-2 border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${
                  passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {passwordErrors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{passwordErrors.confirmPassword}</p>
              )}
            </div>
            <div className="flex space-x-4 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 font-medium transition-colors"
              >
                {saving ? 'Saving...' : 'Update Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordErrors({});
                  setError(null);
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <p className="text-gray-600 text-sm">
            Your password is set. Click "Change Password" to update it.
          </p>
        )}
      </div>

      {/* Quote Defaults */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Quote Calculator Defaults
        </h2>
        <p className="text-sm text-gray-500 mb-4">Set default values that auto-populate when you open the Quote Calculator.</p>
        <QuoteDefaultsSection dealerId={(user as any)?.dealerId} />
      </div>

      {/* Pricing Display */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Pricing Display
        </h2>
        <PricingDisplaySection dealerId={(user as any)?.dealerId || (authUser as any)?.dealerId} />
      </div>

      {/* Preferences */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Email Preferences
        </h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium text-gray-900">Order Confirmations</p>
              <p className="text-sm text-gray-500">Receive confirmation when orders are placed</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" defaultChecked className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium text-gray-900">Production Updates</p>
              <p className="text-sm text-gray-500">Get notified when order status changes</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" defaultChecked className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium text-gray-900">Shipping Notifications</p>
              <p className="text-sm text-gray-500">Get notified when orders ship</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" defaultChecked className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Calendar Sync */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Sync with Your Calendar
        </h2>

        {loadingCalendar ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Subscribe to your Keder calendar to see all your installs, lead follow-ups, and custom events in Google Calendar, Apple Calendar, Outlook, or any calendar app.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription URL
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={subscriptionUrl}
                  className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 font-mono"
                />
                <button
                  onClick={() => copyToClipboard(subscriptionUrl)}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                How to Subscribe
              </h3>
              <div className="text-sm text-blue-800 space-y-2">
                <div>
                  <strong>Google Calendar:</strong>
                  <ol className="list-decimal ml-5 mt-1 space-y-1">
                    <li>Open Google Calendar on your computer</li>
                    <li>Click the "+" next to "Other calendars"</li>
                    <li>Select "From URL"</li>
                    <li>Paste the subscription URL above</li>
                    <li>Click "Add calendar"</li>
                  </ol>
                </div>
                <div className="mt-3">
                  <strong>Apple Calendar:</strong>
                  <ol className="list-decimal ml-5 mt-1 space-y-1">
                    <li>Open Calendar on your Mac or iPhone</li>
                    <li>Go to File → New Calendar Subscription (Mac) or Settings → Accounts → Add Account → Other → Add Subscribed Calendar (iPhone)</li>
                    <li>Paste the subscription URL</li>
                    <li>Click Subscribe</li>
                  </ol>
                </div>
                <div className="mt-3">
                  <strong>Outlook:</strong>
                  <ol className="list-decimal ml-5 mt-1 space-y-1">
                    <li>Open Outlook Calendar</li>
                    <li>Click "Add Calendar" → "Subscribe from web"</li>
                    <li>Paste the subscription URL</li>
                    <li>Name your calendar and click Import</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-gray-600">
                Need a new link? Regenerate if your calendar subscription was compromised.
              </p>
              <button
                onClick={handleRegenerateCalendarToken}
                disabled={regenerating}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {regenerating ? 'Regenerating...' : 'Regenerate Link'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* API Key */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            API Key
          </h2>
          <button onClick={() => setShowGuide(true)} className="text-sm text-yellow-600 hover:underline">Integration Guide</button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Generate a long-lived API key for integrations (Zapier, Make, custom apps). The key is shown once — store it securely.
        </p>

        {generatedKey && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-xs text-green-700 font-medium mb-1">Your new API key (copy now — won't be shown again):</p>
            <div className="flex gap-2">
              <code className="flex-1 text-xs bg-white px-2 py-1 rounded border font-mono text-gray-800 break-all">{generatedKey}</code>
              <button onClick={() => copyToClipboard(generatedKey)} className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700">Copy</button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-4">
          {apiKeyStatus?.hasKey ? (
            <>
              <span className="text-sm text-gray-600">
                Key active {apiKeyStatus.createdAt && <span className="text-gray-400">· created {new Date(apiKeyStatus.createdAt).toLocaleDateString()}</span>}
              </span>
              <button onClick={generateApiKey} disabled={apiKeyLoading} className="px-3 py-1.5 bg-yellow-500 text-black text-sm font-medium rounded-lg hover:bg-yellow-600 disabled:opacity-50">
                {apiKeyLoading ? '...' : 'Regenerate'}
              </button>
              <button onClick={revokeApiKey} disabled={apiKeyLoading} className="px-3 py-1.5 text-red-600 text-sm font-medium hover:bg-red-50 rounded-lg">
                Revoke
              </button>
            </>
          ) : (
            <button onClick={generateApiKey} disabled={apiKeyLoading} className="px-4 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-600 disabled:opacity-50">
              {apiKeyLoading ? 'Generating...' : 'Generate API Key'}
            </button>
          )}
        </div>
      </div>

      {/* Webhooks */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            Webhooks
          </h2>
          {!showWebhookForm && (
            <button onClick={() => setShowWebhookForm(true)} className="px-3 py-1.5 bg-yellow-500 text-black text-sm font-medium rounded-lg hover:bg-yellow-600">
              Add Webhook
            </button>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Receive real-time notifications when events occur (orders, payments, leads). Configure a URL and select which events to subscribe to.
        </p>

        {showWebhookForm && (
          <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL</label>
                <input type="url" value={webhookForm.url} onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })} placeholder="https://your-server.com/webhook" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label (optional)</label>
                <input type="text" value={webhookForm.label} onChange={(e) => setWebhookForm({ ...webhookForm, label: e.target.value })} placeholder="e.g. Zapier, Make, My CRM" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
                <div className="flex flex-wrap gap-2">
                  {WEBHOOK_EVENTS.map(evt => (
                    <label key={evt} className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="checkbox" checked={webhookForm.events.includes(evt)} onChange={(e) => {
                        const events = e.target.checked ? [...webhookForm.events, evt] : webhookForm.events.filter(v => v !== evt);
                        setWebhookForm({ ...webhookForm, events });
                      }} className="w-3.5 h-3.5 rounded border-gray-300 text-yellow-600" />
                      <span className="text-gray-700">{evt}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">Leave empty to receive all events.</p>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={saveWebhook} disabled={webhookSaving || !webhookForm.url} className="px-4 py-2 bg-yellow-500 text-black text-sm font-medium rounded-lg hover:bg-yellow-600 disabled:opacity-50">
                  {webhookSaving ? 'Saving...' : 'Save Webhook'}
                </button>
                <button onClick={() => { setShowWebhookForm(false); setWebhookForm({ url: '', label: '', events: [] }); }} className="px-4 py-2 text-gray-600 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {webhooksLoading ? (
          <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500 mx-auto" /></div>
        ) : webhooks.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No webhooks configured.</p>
        ) : (
          <div className="space-y-3">
            {webhooks.map((wh: any) => (
              <div key={wh.id} className="border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {wh.label && <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{wh.label}</span>}
                    <span className={`w-2 h-2 rounded-full ${wh.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  </div>
                  <p className="text-sm font-mono text-gray-700 truncate mt-1">{wh.url}</p>
                  {wh.events && wh.events.length > 0 && (
                    <p className="text-xs text-gray-400 mt-1">{wh.events.join(', ')}</p>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => testWebhook(wh.id)} className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100">Test</button>
                  <button onClick={() => rotateWebhookSecret(wh.id)} className="px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100">Rotate Secret</button>
                  <button onClick={() => deleteWebhook(wh.id)} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Email Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email Configuration
          </h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Send order emails from your own email address instead of the factory default. Configure SMTP or connect Microsoft 365.
        </p>

        {emailConfig ? (
          <div className="border border-green-200 bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 bg-green-500 rounded-full" />
              <span className="text-sm font-medium text-green-800">Email configured</span>
            </div>
            <p className="text-sm text-green-700 mb-1">Provider: <strong>{emailConfig.provider || 'SMTP'}</strong></p>
            {emailConfig.fromEmail && <p className="text-sm text-green-700">From: <strong>{emailConfig.fromEmail}</strong></p>}
            <div className="flex gap-2 mt-3">
              <button onClick={testEmailConfig} className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">Send Test Email</button>
              <button onClick={deleteEmailConfig} className="px-3 py-1.5 text-red-600 text-sm hover:bg-red-50 rounded-lg">Remove</button>
            </div>
          </div>
        ) : (
          <>
            {!showEmailForm ? (
              <div className="flex gap-3 flex-wrap">
                <button onClick={async () => {
                  try {
                    const data = await apiFetch('/dealer/email-config/microsoft/connect');
                    if (data?.url) window.location.href = data.url;
                  } catch (err: any) {
                    alert(err.message || 'Microsoft connect not available');
                  }
                }} className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 21 21" fill="none"><path d="M0 0h10v10H0z" fill="#f25022"/><path d="M11 0h10v10H11z" fill="#7fba00"/><path d="M0 11h10v10H0z" fill="#00a4ef"/><path d="M11 11h10v10H11z" fill="#ffb900"/></svg>
                  Connect Microsoft 365
                </button>
                <button onClick={() => { setEmailForm({ host: 'smtp.gmail.com', port: 587, user: '', password: '', fromEmail: '', fromName: getField((user as any)?.companyName, (user as any)?.dealerName, '') }); setShowEmailForm(true); }} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 text-sm">Gmail</button>
                <button onClick={() => { setEmailForm({ host: '', port: 587, user: '', password: '', fromEmail: '', fromName: getField((user as any)?.companyName, (user as any)?.dealerName, '') }); setShowEmailForm(true); }} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 text-sm">Other SMTP</button>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
                {emailForm.host === 'smtp.gmail.com' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <strong>Gmail Setup:</strong> Use an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">App Password</a> (not your Google password). Enable 2FA first, then generate an app-specific password.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">SMTP Host</label>
                    <input type="text" value={emailForm.host} onChange={(e) => setEmailForm({ ...emailForm, host: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Port</label>
                    <input type="number" value={emailForm.port} onChange={(e) => setEmailForm({ ...emailForm, port: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Username</label>
                    <input type="text" value={emailForm.user} onChange={(e) => setEmailForm({ ...emailForm, user: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
                    <input type="password" value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">From Email</label>
                    <input type="email" value={emailForm.fromEmail} onChange={(e) => setEmailForm({ ...emailForm, fromEmail: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">From Name</label>
                    <input type="text" value={emailForm.fromName} onChange={(e) => setEmailForm({ ...emailForm, fromName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900" />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={saveEmailConfig} disabled={emailSaving} className="px-4 py-2 bg-yellow-500 text-black text-sm font-medium rounded-lg hover:bg-yellow-600 disabled:opacity-50">{emailSaving ? 'Saving...' : 'Save'}</button>
                  <button onClick={() => setShowEmailForm(false)} className="px-4 py-2 text-gray-600 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Integration Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowGuide(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Integration Guide</h2>
              <button onClick={() => setShowGuide(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            {/* Email Configuration */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Email Configuration</h3>
              <p className="text-sm text-gray-600 mb-2">
                When you send emails from the Email Templates page (sales packets, follow-ups, quotes), they normally come from Keder Factory's address. Connect your own account so customers see your company name and email instead.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-xs text-blue-700"><strong>What it affects:</strong> Only emails you send through your V-Track email templates.</p>
                <p className="text-xs text-blue-700 mt-1"><strong>What it does NOT affect:</strong> Order confirmations, shipping notifications, payment receipts, and password resets — those always come from Keder Factory.</p>
              </div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Setup Options</h4>
              <div className="space-y-2 text-sm text-gray-600 mb-3">
                <p><strong>Microsoft / Outlook:</strong> One-click sign-in. Works with Outlook.com, Microsoft 365, and corporate Exchange accounts.</p>
                <p><strong>Gmail:</strong> Requires a Google App Password (not your regular Gmail password). Enable 2-Step Verification, then generate an App Password at myaccount.google.com/apppasswords.</p>
                <p><strong>Other (SMTP):</strong> Works with any email provider that supports SMTP — Yahoo, GoDaddy, custom domains, etc.</p>
              </div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Common SMTP Settings</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Provider</th><th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Host</th><th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Port</th><th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Notes</th></tr></thead>
                  <tbody className="divide-y">
                    <tr><td className="px-3 py-2 text-gray-700">Gmail</td><td className="px-3 py-2 font-mono text-xs">smtp.gmail.com</td><td className="px-3 py-2">587</td><td className="px-3 py-2 text-xs text-gray-500">Requires App Password (2FA must be enabled)</td></tr>
                    <tr><td className="px-3 py-2 text-gray-700">Outlook / Microsoft 365</td><td className="px-3 py-2 font-mono text-xs">smtp.office365.com</td><td className="px-3 py-2">587</td><td className="px-3 py-2 text-xs text-gray-500">Or use one-click Microsoft connect</td></tr>
                    <tr><td className="px-3 py-2 text-gray-700">Yahoo Mail</td><td className="px-3 py-2 font-mono text-xs">smtp.mail.yahoo.com</td><td className="px-3 py-2">587</td><td className="px-3 py-2 text-xs text-gray-500">Requires App Password</td></tr>
                    <tr><td className="px-3 py-2 text-gray-700">GoDaddy</td><td className="px-3 py-2 font-mono text-xs">smtpout.secureserver.net</td><td className="px-3 py-2">465</td><td className="px-3 py-2 text-xs text-gray-500">Use email account password</td></tr>
                    <tr><td className="px-3 py-2 text-gray-700">Zoho Mail</td><td className="px-3 py-2 font-mono text-xs">smtp.zoho.com</td><td className="px-3 py-2">587</td><td className="px-3 py-2 text-xs text-gray-500">Requires App Password</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Webhooks */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Webhooks</h3>
              <p className="text-sm text-gray-600 mb-2">
                Webhooks push real-time event notifications to your tools. When something happens, we instantly send a JSON payload to a URL you provide.
              </p>
              <div className="bg-gray-50 border rounded-lg p-3 mb-3">
                <p className="text-xs font-medium text-gray-700 mb-1">What You Can Do:</p>
                <ul className="text-xs text-gray-600 list-disc ml-4 space-y-0.5">
                  <li>Auto-create contacts in HubSpot, Salesforce, or Zoho when a lead is created</li>
                  <li>Send Slack notifications when an order ships</li>
                  <li>Log payments to a Google Sheet via Zapier</li>
                  <li>Trigger custom workflows in Make (Integromat)</li>
                </ul>
              </div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Available Events</h4>
              <div className="border rounded-lg overflow-hidden mb-3">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr><th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Event</th><th className="px-3 py-2 text-left text-xs font-medium text-gray-500">When it fires</th><th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Key data</th></tr></thead>
                  <tbody className="divide-y">
                    {[
                      ['order.created', 'New order saved as draft', 'order_number, total'],
                      ['order.submitted', 'Order submitted for review', 'order_number'],
                      ['payment.received', 'Payment confirmed (card, ACH, or invoice)', 'order_number, amount, method'],
                      ['order.in_production', 'Released to factory floor', 'order_number'],
                      ['order.ready', 'All screens built, ready to ship', 'order_number'],
                      ['order.shipped', 'Order shipped or picked up', 'order_number, tracking, carrier'],
                      ['order.delivered', 'Delivery confirmed', 'order_number, delivered_at'],
                      ['order.needs_correction', 'Factory flags an issue', 'order_number, reason'],
                      ['lead.created', 'New lead added in My Leads', 'customer_name, email, phone'],
                      ['lead.updated', 'Lead details or status changed', 'customer_name, status'],
                      ['lead.activity_logged', 'Activity logged on a lead', 'activity_type, summary'],
                    ].map(([event, when, data]) => (
                      <tr key={event}><td className="px-3 py-2 font-mono text-xs text-gray-700">{event}</td><td className="px-3 py-2 text-xs text-gray-600">{when}</td><td className="px-3 py-2 text-xs text-gray-500">{data}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h4 className="text-sm font-semibold text-gray-700 mb-2">Payload Example</h4>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto mb-3">
{`{
  "event": "order.shipped",
  "timestamp": "2026-03-17T14:30:00.000Z",
  "order_id": "a1b2c3d4-...",
  "dealer_id": "e5f6g7h8-...",
  "data": {
    "order_number": "VT26030041",
    "tracking_number": "1Z999AA10123456784",
    "carrier": "UPS Ground"
  }
}`}
              </pre>

              <h4 className="text-sm font-semibold text-gray-700 mb-2">Signature Verification</h4>
              <p className="text-xs text-gray-600 mb-2">Every webhook includes an <code className="bg-gray-100 px-1 rounded">X-Keder-Signature</code> header. Verify it to ensure the payload came from Keder Factory:</p>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto mb-3">
{`const crypto = require('crypto');
const signature = req.headers['x-keder-signature'];
const expected = 'sha256=' + crypto
  .createHmac('sha256', YOUR_WEBHOOK_SECRET)
  .update(JSON.stringify(req.body))
  .digest('hex');
if (signature !== expected) {
  return res.status(401).send('Invalid signature');
}`}
              </pre>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800"><strong>Limitations:</strong> Webhooks are one-way (push only). For two-way sync, use Zapier/Make as middleware — webhook fires to Zapier, Zapier calls our API back with your API key. Retries: up to 5 times with increasing delays (5, 10, 20, 40, 80 min). HTTPS required.</p>
              </div>
            </section>

            {/* API Key */}
            <section className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">API Key</h3>
              <p className="text-sm text-gray-600 mb-2">
                Use an API key to authenticate external tools (Zapier, Make, custom scripts) that need to read from or push data into Keder Factory.
              </p>
              <p className="text-sm text-gray-600 mb-2">Include the key in the <code className="bg-gray-100 px-1 rounded text-xs">Authorization</code> header on every request:</p>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto mb-4">
{`Authorization: Bearer YOUR_API_KEY`}
              </pre>

              {/* Partner API — Real-time pricing & freight */}
              <h4 className="text-sm font-semibold text-gray-700 mt-4 mb-2">Real-Time Pricing &amp; Freight</h4>
              <p className="text-xs text-gray-600 mb-2">Pull dealer-net pricing and live freight quotes without logging into the portal.</p>
              <div className="overflow-x-auto mb-3">
                <table className="min-w-full text-xs border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-2 py-1.5 border-b">Method</th>
                      <th className="text-left px-2 py-1.5 border-b">Endpoint</th>
                      <th className="text-left px-2 py-1.5 border-b">Returns</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    <tr><td className="px-2 py-1 border-b text-green-700">GET</td><td className="px-2 py-1 border-b">/api/partner-api/v1/me</td><td className="px-2 py-1 border-b font-sans">Your dealer profile (discount %, address, flat-rate settings)</td></tr>
                    <tr><td className="px-2 py-1 border-b text-blue-700">POST</td><td className="px-2 py-1 border-b">/api/partner-api/v1/pricing/screen</td><td className="px-2 py-1 border-b font-sans">MSRP + dealer net for a list of screens</td></tr>
                    <tr><td className="px-2 py-1 border-b text-blue-700">POST</td><td className="px-2 py-1 border-b">/api/partner-api/v1/shipping/quote</td><td className="px-2 py-1 border-b font-sans">Live LTL freight quotes for a shipment</td></tr>
                  </tbody>
                </table>
              </div>
              <h5 className="text-xs font-semibold text-gray-600 mt-2 mb-1">Example — Get pricing for 3 screens:</h5>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto mb-2">
{`POST https://dealerportal.kederscreens.com/api/partner-api/v1/pricing/screen
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json

{
  "productType": "cvw",
  "items": [
    { "width": 144, "height": 96, "uChannel": "two_sides", "motorType": "alpha" },
    { "width": 120, "height": 84, "uChannel": "none",      "motorType": "alpha" },
    { "width": 96,  "height": 72, "uChannel": "one_side",  "motorType": "somfy" }
  ],
  "shippingMethod": "shipping"
}`}
              </pre>
              <h5 className="text-xs font-semibold text-gray-600 mt-3 mb-1">Example — Freight quote to ZIP 75166:</h5>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto mb-2">
{`POST https://dealerportal.kederscreens.com/api/partner-api/v1/shipping/quote
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json

{
  "destinationZip": "75166",
  "length": 96, "width": 12, "height": 12,
  "weight": 180, "pallets": 1, "freightClass": 85,
  "accessorials": { "liftgate": true, "residential": false }
}`}
              </pre>

              {/* Partner API — Commission tracking (read-only) */}
              <h4 className="text-sm font-semibold text-gray-700 mt-5 mb-2">Commission Tracking <span className="text-xs font-normal text-gray-500">(read-only)</span></h4>
              <p className="text-xs text-gray-600 mb-2">Pull your sales-rep roster, commission defaults, and commission rows so you can reconcile payouts in your own system. <strong>Keder does not pay these commissions</strong> — they're tracked here for your team's records.</p>
              <div className="overflow-x-auto mb-3">
                <table className="min-w-full text-xs border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-2 py-1.5 border-b">Method</th>
                      <th className="text-left px-2 py-1.5 border-b">Endpoint</th>
                      <th className="text-left px-2 py-1.5 border-b">Returns</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    <tr><td className="px-2 py-1 border-b text-green-700">GET</td><td className="px-2 py-1 border-b">/api/partner-api/v1/sales-reps</td><td className="px-2 py-1 border-b font-sans">Roster (roles, rates, monthly order counts). Optional <code>?status=active</code> / <code>?role=sales_manager</code></td></tr>
                    <tr><td className="px-2 py-1 border-b text-green-700">GET</td><td className="px-2 py-1 border-b">/api/partner-api/v1/commission-settings</td><td className="px-2 py-1 border-b font-sans">Default rates (rep / manager) + lock point</td></tr>
                    <tr><td className="px-2 py-1 border-b text-green-700">GET</td><td className="px-2 py-1 border-b">/api/partner-api/v1/commissions</td><td className="px-2 py-1 border-b font-sans">Commission rows. Filters: <code>salesRepId</code>, <code>status</code>, <code>startDate</code>, <code>endDate</code>, <code>page</code>, <code>limit</code> (max 500)</td></tr>
                    <tr><td className="px-2 py-1 border-b text-green-700">GET</td><td className="px-2 py-1 border-b">/api/partner-api/v1/commissions/summary</td><td className="px-2 py-1 border-b font-sans">Total sales, profit, owed, paid (date-bounded)</td></tr>
                    <tr><td className="px-2 py-1 border-b text-green-700">GET</td><td className="px-2 py-1 border-b">/api/partner-api/v1/commissions/by-rep</td><td className="px-2 py-1 border-b font-sans">Per-rep totals (date-bounded)</td></tr>
                  </tbody>
                </table>
              </div>
              <h5 className="text-xs font-semibold text-gray-600 mt-2 mb-1">Example — Get this quarter's paid commissions:</h5>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto mb-2">
{`GET https://dealerportal.kederscreens.com/api/partner-api/v1/commissions?status=paid&startDate=2026-04-01&endDate=2026-06-30
Authorization: Bearer YOUR_API_KEY`}
              </pre>

              {/* Write — Leads */}
              <h4 className="text-sm font-semibold text-gray-700 mt-5 mb-2">Create a Lead</h4>
              <p className="text-xs text-gray-600 mb-2">Push leads from your website form, CRM, or Zapier into My Leads.</p>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto">
{`POST https://dealerportal.kederscreens.com/api/dealer-leads
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json

{
  "customer_name": "John Smith",
  "email": "john@example.com",
  "phone": "555-0123",
  "lead_source": "website"
}`}
              </pre>

              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Every call is audited</strong> — IP, timestamp, payload, and response summary are recorded. Rate limits apply at the global level (100 req/min). Tokens are valid for 365 days; regenerate at any time to invalidate the old one.
                </p>
              </div>
            </section>

            <button onClick={() => setShowGuide(false)} className="w-full py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-600">Close</button>
          </div>
        </div>
      )}

      {/* Support */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Support
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-900">Contact Us</h3>
            <div className="mt-2 space-y-2 text-sm text-gray-600">
              <p className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="tel:+14699427193" className="hover:text-yellow-600">(469) 942-7193</a>
              </p>
              <p className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:orders@kederscreens.com" className="hover:text-yellow-600">orders@kederscreens.com</a>
              </p>
            </div>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Business Hours</h3>
            <div className="mt-2 space-y-1 text-sm text-gray-600">
              <p>Monday - Friday: 8am - 5pm CT</p>
              <p>Saturday - Sunday: Closed</p>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t">
          <h3 className="font-medium text-gray-900">Factory Address</h3>
          <p className="mt-2 text-sm text-gray-600">
            International Shade Products<br />
            12446 State Hwy 205<br />
            Lavon, TX 75166
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
